<!-- Institute of Design and Innovation (email idi@unm.edu). -->

<!-- List of users page -->
<template>
  <div class="users page">
    <div class="page-heading">
      <h1>Users</h1>
      <av-button @click="shouldShowNewUserForm = true"><i class="fal fa-plus-circle"></i> Add User</av-button>
    </div>

    <div class="page-content">
      <!-- List of Users -->
      <div v-for="user of users" :key="user.id" class="page-listing user-listing">
        <div class="name">{{ user.firstName }} {{ user.lastName }}</div>
        <div class="permissions"><i class="fal fa-key"></i> {{ permissionString(user) }}</div>
        <div class="actions">
          <router-link :to="'/users/' + user.id"><a class="shaded black"><i class="fal fa-edit"></i> Edit</a></router-link>
          <a class="shaded red" @click="removeUser(user)">Remove</a>
        </div>
      </div>

      <!-- Form to search and add user as OSP Staff -->
      <av-modal :visible="shouldShowNewUserForm" @close="resetNewUserForm">
        <div class="modal-header">Add New User</div>
        <div class="modal-content">
          <input type="text" v-model="newUserUsername" placeholder="username" class="av-input">
          <div v-if="userResult" class="user-result">
            <h4>{{ userResult.firstName }} {{ userResult.lastName }}</h4>
            <p><b>Banner ID:</b> {{ userResult.id }}</p>
          </div>
          <p v-if="!userResult && newUserUsername !== '' && !searchingForUser">No User Found</p>
          <p v-if="searchingForUser">Searching...</p>
        </div>
        <div class="modal-actions">
          <av-button class="hollow" color="error" @click="resetNewUserForm()">Cancel</av-button>
          <av-button @click="addOspStaff" :disabled="!userResult">Create</av-button>
        </div>
      </av-modal>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import UserService from '../common/userService'
import User from '../models/user'
import { debounce } from 'lodash'

export default defineComponent({
  name: 'Users',

  data () {
    return {
      /** The username to search on */
      newUserUsername: '',

      /** Indicates that a user search is in process */
      searchingForUser: false,

      /** Indicator to show or hide the new user form */
      shouldShowNewUserForm: false,

      /** The result of the user search */
      userResult: null as unknown as User | null,

      /** The list of users */
      users: [] as Array<User>
    }
  },

  mounted () {
    // Load the current users
    UserService.getOspStaff().then(res => {
      this.users = res.data
    })
  },

  watch: {
    // Perform a debounced search when the username input chagnes
    newUserUsername: function (username: string) {
      this.userResult = null

      if (username !== '') {
        this.searchingForUser = true
        this.debouncedSearch(username)
      } else {
        this.searchingForUser = false
      }
    }
  },

  methods: {
    /**
     * Performs a debounced search for a user
     * @param username: The username to search on
     */
    debouncedSearch: debounce(function (this: { search }, username: string) {
      this.search(username)
    }, 1000),

    /**
     * Adds the user found via search as an OSP staff member
     */
    addOspStaff () {
      if (this.userResult) {
        UserService.createOspStaff(this.userResult.username).then(res => {
          this.users.push(res.data)
          this.resetNewUserForm()
        })
      }
    },

    /**
     * Returns's a comma separated list of a user's permissions
     * @param user: Tthe user
     */
    permissionString (user: User) {
      return user.permissions.map(p => p.name).join(', ')
    },

    /**
     * Removes OSP Staff status from the given user
     * @param user: The user to remove
     */
    removeUser (user: User) {
      if (window.confirm('Are you sure?')) {
        UserService.removeOspStaff(user.id).then(() => {
          const ind = this.users.findIndex(u => u.id === user.id)
          this.users.splice(ind, 1)
        })
      }
    },

    /**
     * Resets the new user form
     */
    resetNewUserForm () {
      this.newUserUsername = ''
      this.shouldShowNewUserForm = false
    },

    /**
     * Performs a search for a user given a username
     * @param username: The username to search on
     */
    search (username: string) {
      UserService.searchUser(username).then(res => {
        this.userResult = res.data
        this.searchingForUser = false
      }).catch(() => {
        this.userResult = null
        this.searchingForUser = false
      })
    }
  }
})
</script>
